import { useMemo, useState } from 'react'
import { useTenant } from '@/hooks/use-tenant'
import { useGetPublicImageResourceQuery } from '@/state/user/client-api'
import { Button } from '@/components/ui/button'
import { Image as ImageIcon, Loader2 } from 'lucide-react'
import { useGetCurrentUserQuery } from '@/state'

interface UsersLatestUploadsProps {
    onClick: (upload: { image_path: string }) => void
}

export default function UsersLatestUploads({ onClick }: UsersLatestUploadsProps) {
    const { tenant } = useTenant()
    const { data: user, isLoading: isUserLoading } = useGetCurrentUserQuery({ tenant })
    const [pageSize, setPageSize] = useState<number | undefined>(3)

    const { data: imagesData, isLoading: isImagesLoading } = useGetPublicImageResourceQuery(
        { userId: user?.id || '', pageSize: pageSize },
        { skip: !user?.id }
    )

    const latestUploads = useMemo(() => {
        if (!imagesData) return []
        // Assess the structure of imagesData. Assuming it might be an array or { results: [...] }
        // Based on other endpoints, it's likely the response itself or a property.
        // If it returns a list directly:
        if (Array.isArray(imagesData)) return imagesData
        // If paginated response:
        if (Array.isArray(imagesData?.results)) return imagesData.results
        if (Array.isArray(imagesData?.data)) return imagesData.data
        return []
    }, [imagesData])

    const isLoading = isUserLoading || isImagesLoading

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-4">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
            </div>
        )
    }

    if (latestUploads.length === 0) {
        return (
            <div className="mb-4 p-4 border-2 border-dashed border-gray-200 rounded-lg text-center">
                <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground/60 mb-2" />
                <p className="text-sm text-muted-foreground">No public images found</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Upload an image to see it here</p>
            </div>
        )
    }

    return (
        <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-foreground">Recent Uploads</p>
                {pageSize === 3 && (
                    <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs text-blue-600"
                        onClick={() => setPageSize(undefined)}
                    >
                        See All
                    </Button>
                )}
                {pageSize === undefined && latestUploads.length > 3 && (
                    <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs text-blue-600"
                        onClick={() => setPageSize(3)}
                    >
                        Show Less
                    </Button>
                )}
            </div>
            <div className="grid grid-cols-4 gap-2 max-h-[300px] overflow-y-auto pr-1">
                {latestUploads.map((upload: any, index: number) => (
                    <button
                        key={upload.id || index}
                        className="relative aspect-square rounded-md overflow-hidden border border-gray-200 hover:border-blue-500 transition-colors group"
                        onClick={() => onClick({ image_path: upload.image_path || upload.url || upload.file })}
                    >
                        <img
                            src={upload.image_path || upload.url || upload.file}
                            alt={`Upload ${index}`}
                            className="object-cover w-full h-full"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white text-xs font-medium">Select</span>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    )
}
